/**
 * Login Widget — shared auth UI for both the standalone login page and the
 * session-expired modal. Self-contained (no AppLib/AppServices deps).
 */

import { loadFirebase, exchangeFirebaseToken } from './firebase-auth';

// Inline spinner styles injected once per page
const SPINNER_CSS = `
@keyframes lw-spin { to { transform: rotate(360deg); } }
.lw-spinner {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid #ccc;
  border-top-color: #004E92;
  border-radius: 50%;
  animation: lw-spin .7s linear infinite;
  vertical-align: middle;
  flex-shrink: 0;
}`;

let spinnerInjected = false;
function ensureSpinnerStyles(): void {
  if (spinnerInjected) return;
  const style = document.createElement('style');
  style.textContent = SPINNER_CSS;
  document.head.appendChild(style);
  spinnerInjected = true;
}

const APPLE_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" style="flex-shrink:0">
  <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.459 2.208 3.09 3.792 3.029 1.52-.065 2.09-.987 3.925-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
</svg>`;

const MICROSOFT_SVG = `<svg viewBox="0 0 21 21" width="18" height="18" aria-hidden="true" style="flex-shrink:0">
  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
</svg>`;

const GOOGLE_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" style="flex-shrink:0">
  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
</svg>`;

function handleSuccess(requiresTenantSelection: boolean): void {
  if (requiresTenantSelection) {
    window.location.href = '/select-tenant';
  } else {
    window.location.reload();
  }
}

// ── initPage ──────────────────────────────────────────────────────────────────
// Attaches the Firebase/Google handler on the standalone login.html page.
// The email/password form is a native <form method="post"> handled by Flask.

export interface InitPageOptions {
  /** Pass "register" to call the firebase-login endpoint in register mode. */
  mode?: string;
}

export function initPage(options: InitPageOptions = {}): void {
  loadFirebase().then((firebase) => {
    if (!firebase) return;

    const socialSection = document.getElementById('social-login');
    const googleBtn = document.getElementById('btn-google') as HTMLButtonElement | null;
    const appleBtn = document.getElementById('btn-apple') as HTMLButtonElement | null;
    const microsoftBtn = document.getElementById('btn-microsoft') as HTMLButtonElement | null;
    const errorEl = document.getElementById('social-error');

    if (!socialSection || !googleBtn || !errorEl) return;

    ensureSpinnerStyles();
    socialSection.style.display = 'block';

    async function handleSocialClick(
      btn: HTMLButtonElement,
      getToken: () => Promise<string>,
    ): Promise<void> {
      errorEl!.style.display = 'none';
      btn.disabled = true;
      const originalHtml = btn.innerHTML;
      btn.innerHTML = '<span class="lw-spinner"></span>';

      try {
        const firebaseToken = await getToken();
        const result = await exchangeFirebaseToken(firebaseToken, options.mode);
        window.location.href = result.requires_tenant_selection ? '/select-tenant' : '/';
      } catch (err) {
        const msg =
          (err as { code?: string }).code === 'auth/popup-closed-by-user'
            ? 'Sign-in cancelled.'
            : ((err as Error).message || 'Sign in failed. Please try again.');
        errorEl!.textContent = msg;
        errorEl!.style.display = 'block';
        btn.disabled = false;
        btn.innerHTML = originalHtml;
      }
    }

    googleBtn.addEventListener('click', () => void handleSocialClick(googleBtn, () => firebase.signInWithGoogle()));

    if (appleBtn) {
      appleBtn.addEventListener('click', () => void handleSocialClick(appleBtn, () => firebase.signInWithApple()));
    }

    if (microsoftBtn) {
      microsoftBtn.addEventListener('click', () => void handleSocialClick(microsoftBtn, () => firebase.signInWithMicrosoft()));
    }
  });
}

// ── showModal ─────────────────────────────────────────────────────────────────
// Creates a non-dismissible session-expired modal with password + Google auth.
// Replaces the inline modal previously in static/apps/lib/api.js.

export function showModal(): void {
  ensureSpinnerStyles();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay session-expired-modal is-visible';
  overlay.innerHTML = `
    <div class="modal-container" style="max-width:400px">
      <div class="modal-header">
        <h2 class="modal-title">Session Expired</h2>
      </div>
      <div class="modal-content">
        <p style="margin:0 0 16px;color:var(--text-muted)">
          Your session has expired. Please sign in again to continue.
        </p>
        <form id="lw-form" style="display:flex;flex-direction:column;gap:14px">
          <div class="modal-section" style="gap:6px">
            <label class="modal-label" for="lw-email">Email</label>
            <input type="email" id="lw-email" class="modal-input" autocomplete="email" required />
          </div>
          <div class="modal-section" style="gap:6px">
            <label class="modal-label" for="lw-password">Password</label>
            <input type="password" id="lw-password" class="modal-input" autocomplete="current-password" required />
          </div>
          <div id="lw-error" style="display:none;color:var(--color-danger,#dc3545);font-size:0.875rem"></div>
        </form>
        <div id="lw-social" style="display:none">
          <div class="modal-divider">or</div>
          <button id="lw-google" class="tenant-btn" style="width:100%;justify-content:center;gap:8px">
            ${GOOGLE_SVG}
            Continue with Google
          </button>
          <button id="lw-apple" class="tenant-btn" style="width:100%;justify-content:center;gap:8px;margin-top:8px">
            ${APPLE_SVG}
            Continue with Apple
          </button>
          <button id="lw-microsoft" class="tenant-btn" style="width:100%;justify-content:center;gap:8px;margin-top:8px">
            ${MICROSOFT_SVG}
            Continue with Microsoft
          </button>
        </div>
      </div>
      <div class="modal-footer">
        <button id="lw-submit" class="tenant-btn tenant-btn--primary" style="flex:1">Sign In</button>
      </div>
    </div>
  `;

  const form = overlay.querySelector<HTMLFormElement>('#lw-form')!;
  const emailInput = overlay.querySelector<HTMLInputElement>('#lw-email')!;
  const passwordInput = overlay.querySelector<HTMLInputElement>('#lw-password')!;
  const errorEl = overlay.querySelector<HTMLElement>('#lw-error')!;
  const submitBtn = overlay.querySelector<HTMLButtonElement>('#lw-submit')!;
  const socialSection = overlay.querySelector<HTMLElement>('#lw-social')!;

  function showError(msg: string): void {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  }

  function clearError(): void {
    errorEl.style.display = 'none';
  }

  async function submitPassword(): Promise<void> {
    clearError();
    submitBtn.disabled = true;
    submitBtn.textContent = 'Signing in\u2026';

    try {
      const resp = await fetch('/api/users/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput.value.trim(), password: passwordInput.value }),
      });

      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        handleSuccess(Boolean((data as { requires_tenant_selection?: boolean }).requires_tenant_selection));
      } else {
        const data = await resp.json().catch(() => ({}));
        showError((data as { detail?: string }).detail || 'Invalid email or password');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Sign In';
      }
    } catch {
      showError('Connection error. Please try again.');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Sign In';
    }
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void submitPassword();
  });

  submitBtn.addEventListener('click', (e) => {
    e.preventDefault();
    if (form.checkValidity()) {
      void submitPassword();
    } else {
      form.reportValidity();
    }
  });

  // ── Social login (async — shows after Firebase SDK loads) ─────────────────
  loadFirebase().then((firebase) => {
    if (!firebase) return;

    socialSection.style.display = 'block';

    async function handleModalSocial(
      btn: HTMLButtonElement,
      getToken: () => Promise<string>,
    ): Promise<void> {
      clearError();
      btn.disabled = true;
      const originalHtml = btn.innerHTML;
      btn.innerHTML = `<span class="lw-spinner"></span> Signing in\u2026`;

      try {
        const firebaseToken = await getToken();
        const result = await exchangeFirebaseToken(firebaseToken);
        handleSuccess(result.requires_tenant_selection);
      } catch (err) {
        const msg =
          (err as { code?: string }).code === 'auth/popup-closed-by-user'
            ? 'Sign-in cancelled.'
            : ((err as Error).message || 'Sign in failed. Please try again.');
        showError(msg);
        btn.disabled = false;
        btn.innerHTML = originalHtml;
      }
    }

    const googleBtn = overlay.querySelector<HTMLButtonElement>('#lw-google')!;
    const appleBtn = overlay.querySelector<HTMLButtonElement>('#lw-apple')!;
    const microsoftBtn = overlay.querySelector<HTMLButtonElement>('#lw-microsoft')!;

    googleBtn.addEventListener('click', () => void handleModalSocial(googleBtn, () => firebase.signInWithGoogle()));
    appleBtn.addEventListener('click', () => void handleModalSocial(appleBtn, () => firebase.signInWithApple()));
    microsoftBtn.addEventListener('click', () => void handleModalSocial(microsoftBtn, () => firebase.signInWithMicrosoft()));
  });

  document.body.appendChild(overlay);
  emailInput.focus();
}
